/**
 * Sanitização de campos de texto no frontend (produtos / insumos).
 */

/** Linha única: sem espaço ou quebra no início; sem quebras no meio. */
export function onChangeSingleLineText(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/^[\s\r\n]+/, '').replace(/[\r\n]/g, ' ');
}

/** Normaliza ao sair do campo ou antes de salvar. */
export function sanitizeSingleLineText(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Textarea: remove espaços no início/fim de cada linha. */
export function sanitizeMultiLineText(value) {
  if (typeof value !== 'string') return '';
  return value
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

export function onChangeMultiLineText(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/^[\s\r\n]+/, '');
}

/** Aplica sanitização no payload enviado às Cloud Functions. */
export function sanitizeProductSavePayload(data, { isGlobal = false } = {}) {
  if (!data || typeof data !== 'object') return data;
  const out = { ...data };

  const singleFields = isGlobal
    ? ['nome', 'nome_produto', 'codigo', 'codigo_produto', 'categoria', 'subcategoria', 'industria', 'unidade_medida', 'fonte', 'referencia_normativa', 'frequencia_execucao']
  : ['nome', 'codigo', 'categoria', 'subcategoria', 'industria', 'unidade_medida', 'frequencia_execucao'];

  for (const key of singleFields) {
    if (typeof out[key] === 'string') {
      out[key] = sanitizeSingleLineText(out[key]);
    }
  }

  if (typeof out.descricao === 'string') {
    out.descricao = sanitizeMultiLineText(out.descricao);
  }
  if (typeof out.observacoes === 'string') {
    out.observacoes = sanitizeMultiLineText(out.observacoes);
  }
  if (typeof out.notas === 'string') {
    out.notas = sanitizeMultiLineText(out.notas);
  }

  if (isGlobal && typeof out.nome_produto === 'string' && !out.nome) {
    out.nome = out.nome_produto;
  }
  if (isGlobal && typeof out.nome === 'string') {
    out.nome_produto = sanitizeSingleLineText(out.nome);
  }

  if (Array.isArray(out.sinonimos)) {
    out.sinonimos = out.sinonimos
      .map((s) => sanitizeSingleLineText(String(s)))
      .filter(Boolean);
  }

  const params = out.parametros_qualidade || out.parametros;
  if (Array.isArray(params)) {
    const cleaned = params.map((p) =>
      typeof p?.nome === 'string' ? { ...p, nome: sanitizeSingleLineText(p.nome) } : p
    );
    if (out.parametros_qualidade) out.parametros_qualidade = cleaned;
    if (out.parametros) out.parametros = cleaned;
  }

  return out;
}

/** Texto para comparação em filtros/busca (sem acentos, minúsculas, espaços colapsados). */
export function normalizeForSearch(input) {
  const base = sanitizeSingleLineText(String(input ?? ''));
  if (!base) return '';
  return base
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function extractDigitsForSearch(input) {
  return String(input ?? '').replace(/\D/g, '');
}

export function sanitizeSearchQuery(query) {
  return sanitizeSingleLineText(String(query ?? ''));
}

export function matchesSearchTerm(haystack, query, options = {}) {
  const q = normalizeForSearch(query);
  if (!q) return true;

  const h = normalizeForSearch(haystack);
  if (!h) return false;

  // Match contínuo (ex.: "pulmao suino" em "pulmao suino congelado")
  if (h.includes(q)) return true;

  // Match por tokens: todas as palavras do termo precisam aparecer (ordem livre).
  // Ex.: "farinha trigo" encontra "Farinha de Trigo".
  const tokens = q.split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length > 1 && tokens.every((t) => h.includes(t))) return true;

  if (options.matchDigits) {
    const qd = extractDigitsForSearch(query);
    const hd = extractDigitsForSearch(haystack);
    if (qd.length > 0 && hd.includes(qd)) return true;
  }

  return false;
}

export function recordMatchesSearch(record, query, fields, options = {}) {
  const q = normalizeForSearch(query);
  if (!q) return true;

  return fields.some((field) => {
    const val = record?.[field];
    if (matchesSearchTerm(val, query, options)) return true;
    if (options.matchDigits && extractDigitsForSearch(val).includes(extractDigitsForSearch(query))) {
      return true;
    }
    return false;
  });
}

/** Filtra lista local por getters de campos (nome, código, etc.). */
export function filterItemsBySearch(items, query, fieldGetters, options = {}) {
  const q = normalizeForSearch(query);
  if (!q) return items;
  const getters = Array.isArray(fieldGetters) ? fieldGetters : [fieldGetters];

  return (items || []).filter((item) =>
    getters.some((getVal) => {
      const raw = typeof getVal === 'function' ? getVal(item) : item?.[getVal];
      if (Array.isArray(raw)) {
        return raw.some((v) => matchesSearchTerm(v, query, options));
      }
      return matchesSearchTerm(raw, query, options);
    })
  );
}
